import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';

import { useBridgeApi, useDispatch, useLogger, useSelector } from 'Hooks';
import * as actions from 'actions';
import { useShoppingCartApi } from 'Components/ShoppingCartUniverse';
import { HOMEPAGE_QUERY } from 'Scenes/Home';
import { ServiceType } from 'Scenes/StoreListing';
import { useStorage } from 'Components/Storage';

import { GetTableNumber, GetTableNumberVariables } from './__queries__';
import TableLandingView from './view';
import { Language } from '../../Types';
import { useConfig } from 'Components/ConfigProvider';

interface Props {
  tableId: string | null;
  venueId: string;
}

export const TABLE_QUERY = gql`
  query GetTableNumber($tableId: ID!) {
    Table(id: $tableId) {
      id
      code
    }
  }
`;

const TableLandingPage = ({ venueId, tableId }: Props) => {
  const config = useConfig();
  const logger = useLogger('table-landing-page');
  const api = useShoppingCartApi();
  const bridgeApi = useBridgeApi();
  const [, updateStorage] = useStorage();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data } = useQuery<GetTableNumber, GetTableNumberVariables>(TABLE_QUERY, {
    variables: {
      tableId: tableId ?? '',
    },
    skip: !tableId,
  });
  const tableNumber = data?.Table?.code;
  const serviceTypes = config.ALLOWED_SERVICE_TYPES;
  const currentLanguage = useSelector((state) => state.language);
  const [modalLanguage, setModalLanguage] = useState<Language>(currentLanguage);
  const [showLanguageDialog, setShowLanguageDialog] = useState<boolean>(false);

  const createCartWithTable = () => {
    // Set up our cart with tableId as deliverytargetId
    const id = api.createCart(venueId, 'EAT_HERE', tableId);
    logger.info('Created cart with Table Id', {
      tableId,
      tableNumber,
      venueId,
      cartId: id,
    });
    bridgeApi.vibrate('selection');
    // Set QR order to true in global state
    dispatch(actions.setqrOrder(true));
    updateStorage({
      serviceType: 'EAT_HERE',
      qrOrder: true,
    });

    history.push({
      pathname: `/order/${venueId}`,
      search: `?type=EAT_HERE&cartId=${id}`,
    });
  };

  const redirectToLogin = () => history.push({ pathname: '/signin', search: `?v=${venueId}` });

  const createCartNoTable = (serviceType: ServiceType) => {
    // Set up our cart
    const id = api.createCart(venueId, serviceType, null);
    logger.log('Created cart without Table Id', {
      venueId,
      cartId: id,
    });
    // Set QR order to true in global state
    dispatch(actions.setqrOrder(true));
    updateStorage({
      serviceType,
      qrOrder: true,
    });

    history.push({ pathname: `/order/${venueId}`, search: `?type=${serviceType}` });
  };

  // Preload the homepage query
  useQuery(HOMEPAGE_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!tableId) {
      return;
    }

    dispatch(actions.setTableId(tableId));
  }, [tableId]);

  const handleShowLanguageDialog = () => {
    setShowLanguageDialog(true);
    setModalLanguage(currentLanguage);
  };

  const handleSelectLanguage = () => {
    if (currentLanguage !== modalLanguage) {
      dispatch(actions.changeLanguage(modalLanguage));
    }

    setShowLanguageDialog(false);
  };

  const handleCloseLanguageDialog = () => {
    setShowLanguageDialog(false);
  };

  return (
    <TableLandingView
      tableId={tableId}
      venueId={venueId}
      tableNumber={tableNumber}
      dark={true}
      createCartWithTable={createCartWithTable}
      createCartNoTable={createCartNoTable}
      redirectToLogin={redirectToLogin}
      serviceTypes={serviceTypes}
      modalLanguage={modalLanguage}
      setModalLanguage={setModalLanguage}
      onSelectLanguage={handleSelectLanguage}
      showLanguageDialog={showLanguageDialog}
      onShowLanguageDialog={handleShowLanguageDialog}
      handleCloseLanguageDialog={handleCloseLanguageDialog}
    />
  );
};

export default TableLandingPage;
